import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentHistoryDTO } from "../../../../models/EnrollmentHistoryDTO";
import { EnrollmentDueDayDTO } from "../../../../models/EnrollmentDueDayDTO";
import { ReentryInscriptionDTO, TargetSchedule, UPDATE_TYPE, UpdateOptionsDTO } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import PromocionDirecta, { faltaPromo, promoParaEnviar, promoVacia } from "../../../../components/acuerdos/promocionDirecta";
import InscripcionReingreso, { promoDeReingreso } from "../../../../components/acuerdos/inscripcionReingreso";
import { RequestPromotionDTO } from "../../../../models/EnrollmentHistoryDTO";
import BuscadorHorarios, { DetalleHorario, HorarioFila, filaDeDestino } from "../../../../components/horarios/buscadorHorarios";
import S from "./strings";
import { CHIP_VENCIMIENTO, TabProps, nombreHorario, TYPE_LABELS, TYPE_ORDER, dinero, enrollmentStatusLabel, errorText, needsSchedule, planLinea, sessionDelta } from "./helpers";
import { UpdateDetail } from "./piezas";

// Estatus desde los que se pide una actualización: Activo, Aplazado, Sin horario (y Retirado para Reingreso).
const UPDATABLE_STATUSES = [1, 7, 9, 6];

interface Encontrada { id: number; name: string; code: string; phone: string; }

interface Props extends TabProps { initialStudentId?: number; initialEnrollmentId?: number; }

/** Actualizar, Una Alumna: busca a la alumna, ve su matrícula y plan, elige qué cambia y el horario destino con el Buscador de Horarios. */
const UnaAlumna: React.FC<Props> = ({ campusId, countryId, canApplyNow, notify, changed, initialStudentId, initialEnrollmentId }) => {
	const http = HttpManager.getInstance();
	const [busy, setBusy] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");
	const [found, setFound] = useState<Encontrada[] | null>(null);
	const [studentId, setStudentId] = useState<number | null>(initialStudentId ?? null);
	const [history, setHistory] = useState<EnrollmentHistoryDTO[] | null>(null);
	const [enrollmentId, setEnrollmentId] = useState<number | null>(initialEnrollmentId ?? null);
	const [options, setOptions] = useState<UpdateOptionsDTO | null>(null);
	const [attendance, setAttendance] = useState<number | null>(null);
	const [due, setDue] = useState<EnrollmentDueDayDTO | null>(null);

	const [typeId, setTypeId] = useState<number | null>(null);
	const [destino, setDestino] = useState<HorarioFila | null>(null);
	const [buscando, setBuscando] = useState<boolean>(true);
	const [toScheduleId, setToScheduleId] = useState<number | null>(null);
	const [toCourseId, setToCourseId] = useState<number | null>(null);
	const [lastSession, setLastSession] = useState<string>("");
	const [dueDayDraft, setDueDayDraft] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [createdId, setCreatedId] = useState<number | null>(null);
	const [promo, setPromo] = useState<RequestPromotionDTO>(promoVacia());
	// Reingreso: inscripción nueva a precio de lista del horario destino; null = promo de inscripción por defecto, 0 = quitada.
	const [inscReingreso, setInscReingreso] = useState<ReentryInscriptionDTO | null>(null);
	const [insc, setInsc] = useState<number | null>(null);

	const resetDecision = () => { setInsc(null); setInscReingreso(null); setPromo(promoVacia()); setTypeId(null); setDestino(null); setBuscando(true); setToScheduleId(null); setToCourseId(null); setLastSession(""); setDueDayDraft(""); setNote(""); setCreatedId(null); };
	const resetAll = () => { resetDecision(); setOptions(null); setEnrollmentId(null); setHistory(null); setStudentId(null); setFound(null); setSearch(""); setAttendance(null); setDue(null); };

	// Búsqueda al escribir (con pausa), nunca por reloj.
	useEffect(() => {
		const texto = search.trim();
		if (texto.length < 2 || studentId) { setFound(null); return; }
		let vigente = true;
		const espera = setTimeout(() => {
			http.studentService.getStudentsByCampusId(campusId, 10, { search: texto })
				.then((r) => vigente && setFound((r.data?.data || []).map((s: any) => ({ id: s.id, name: `${s.firstName} ${s.lastName}`.trim(), code: s.identityCode || s.correlative || "", phone: s.phone1 || s.phone || "" }))))
				.catch((e) => vigente && notify(errorText(e), false));
		}, 300);
		return () => { vigente = false; clearTimeout(espera); };
	}, [search, campusId, studentId]);

	useEffect(() => {
		if (!studentId) { setHistory(null); return; }
		let vigente = true;
		http.enrollmentService.getStudentHistory(campusId, studentId).then((r) => {
			if (!vigente) { return; }
			const list = (r.data || []).filter((e) => e.isCurrentCampus && UPDATABLE_STATUSES.includes(e.statusId));
			setHistory(list);
			if (!enrollmentId || !list.some((e) => e.id === enrollmentId)) { setEnrollmentId(list.length === 1 ? list[0].id : null); }
		}).catch((e) => vigente && notify(errorText(e), false));
		return () => { vigente = false; };
	}, [campusId, studentId]);

	useEffect(() => {
		if (!enrollmentId) { setOptions(null); setAttendance(null); setDue(null); return; }
		let vigente = true;
		setOptions(null); setAttendance(null); setDue(null);
		http.enrollmentUpdatesService.options(campusId, enrollmentId).then((r) => {
			if (!vigente) { return; }
			setOptions(r.data);
			setSearch((s) => s || r.data.enrollment.studentName || "");
			if (r.data.enrollment.scheduleId) {
				http.enrollmentUpdatesService.scheduleRoster(campusId, r.data.enrollment.scheduleId)
					.then((x) => vigente && setAttendance(x.data.rows.find((f) => f.enrollmentId === enrollmentId)?.attendancePct ?? null))
					.catch(() => { /* sin asistencia: la ficha se ve igual */ });
			}
		}).catch((e) => vigente && notify(errorText(e), false));
		http.enrollmentUpdatesService.getDueDay(campusId, enrollmentId).then((r) => vigente && setDue(r.data)).catch(() => { /* sin permiso de vencimiento: no sale el chip */ });
		return () => { vigente = false; };
	}, [campusId, enrollmentId]);

	const isCourse = typeId === UPDATE_TYPE.COURSE_CHANGE;
	const isReentry = typeId === UPDATE_TYPE.REENTRY;
	useEffect(() => {
		setInsc(null); setInscReingreso(null);
		if (!isReentry || !toScheduleId || !enrollmentId) { return; }
		let vigente = true;
		http.enrollmentUpdatesService.reentryProjection(campusId, enrollmentId, toScheduleId)
			.then((r) => vigente && setInscReingreso(r.data.inscription || null))
			.catch((x) => vigente && notify(errorText(x), false));
		return () => { vigente = false; };
	}, [isReentry, toScheduleId, enrollmentId, campusId]);
	const isNextBlock = typeId === UPDATE_TYPE.NEXT_BLOCK;
	const destinos: TargetSchedule[] = useMemo(() => !options ? [] : isCourse ? options.courses.flatMap((c) => c.schedules)
		: isNextBlock ? (options.nextBlockSchedules || []) : options.targetSchedules, [options, isCourse, isNextBlock]);
	const cursoDe = (scheduleId: number | null) => options?.courses.find((c) => c.schedules.some((s) => s.id === scheduleId));
	const soloIds = useMemo(() => destinos.map((d) => d.id), [destinos]);
	const complemento = useMemo(() => destinos.map((t) => filaDeDestino(t, isCourse ? cursoCorto(options?.courses.find((c) => c.schedules.some((s) => s.id === t.id))?.name)
		: isNextBlock ? "Bloque " + ((t as any).blockName || "") : cursoCorto(options?.enrollment.courseName), campusId)), [destinos, options, isCourse, isNextBlock, campusId]);
	const detalle = useMemo(() => {
		const out: { [id: number]: DetalleHorario } = {};
		destinos.forEach((t) => { out[t.id] = { cupo: t.limit || null, alumnos: t.enrolled, lleno: t.isFull, sesion: t.classesGiven + 1 }; });
		return out;
	}, [destinos]);
	const target: TargetSchedule | undefined = destinos.find((t) => t.id === toScheduleId);

	const e = options?.enrollment;
	const allowed = (options?.types || []).filter((t) => t.allowed).map((t) => t.id);
	const chips: number[] = [...TYPE_ORDER.filter((id) => allowed.includes(id)), ...(due?.canEdit && e?.statusId === 1 ? [CHIP_VENCIMIENTO] : [])];
	const pendingAlready = !!options?.pendingUpdateId;
	const taken = e?.sessionsTaken ?? 0;
	const delta = target && !isCourse ? sessionDelta(taken, target.classesGiven) : { less: 0, more: 0 };
	const destOk = !needsSchedule(typeId) || (!!toScheduleId && (!isCourse || (!!toCourseId && !faltaPromo(promo, [toScheduleId]))));
	const noteOk = note.trim().length >= 3;
	const dayNum = Number(dueDayDraft);
	const dueOk = typeId === CHIP_VENCIMIENTO && Number.isInteger(dayNum) && dayNum >= 1 && dayNum <= 28;
	const canSend = !!e && typeId !== null && typeId !== CHIP_VENCIMIENTO && destOk && noteOk && !busy && !pendingAlready && !createdId;
	const canNow = canSend && canApplyNow && (options?.isCaja || (options?.isAcademic && [1, 2, 5].includes(typeId || 0))) && delta.more === 0;

	const send = (applyNow: boolean) => {
		if (!e || typeId === null) { return; }
		setBusy(true);
		http.enrollmentUpdatesService.create(campusId, { applyNow: applyNow && !isCourse, items: [{
			enrollmentId: e.id, typeId, note: note.trim(),
			toScheduleId: needsSchedule(typeId) ? toScheduleId : null, toCourseId: isCourse ? toCourseId : null,
			promotion: isCourse && toScheduleId ? promoParaEnviar(promo, toScheduleId, {}, true) : isReentry && toScheduleId ? promoDeReingreso(toScheduleId, insc) : undefined,
			lastSessionTaken: typeId === UPDATE_TYPE.RETIRED && lastSession ? Number(lastSession) : null
		}] })
			.then((r) => {
				changed();
				// Cambio a otro curso: el reparto se arma sobre la actualización ya creada.
				if (isCourse && applyNow && r.data.updates?.[0]) { setCreatedId(r.data.updates[0].id); notify(S.sent(1)); return; }
				// Lo que dice el servidor manda: sin permiso de aplicar (o si toca dinero) la actualización queda Por Aplicar.
				const quedoPendiente = (r.data.updates || []).some((u) => u.statusId === 1);
				notify(applyNow && !quedoPendiente ? S.applied : S.sent(1));
				resetAll();
			})
			.catch((x) => notify(errorText(x), false))
			.finally(() => setBusy(false));
	};

	const saveDue = () => {
		if (!e || !dueOk || !noteOk) { return; }
		setBusy(true);
		http.enrollmentUpdatesService.setDueDay(campusId, { enrollmentId: e.id, dueDay: dayNum, note: note.trim() })
			.then((r) => { setDue(r.data); notify(S.dueDaySaved); setNote(""); setDueDayDraft(""); setTypeId(null); })
			.catch((x) => notify(errorText(x), false))
			.finally(() => setBusy(false));
	};

	const plan = e ? planLinea(e.tuitions) : null;
	const sesionesTotal = e?.sessionsTotal || 0;

	return (
		<div className="wau-caja wau-pila">
			<div className="wau-buscar">
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
				<input value={search} placeholder={S.searchPlaceholder} aria-label={S.searchStudent} autoFocus={!initialStudentId}
					onChange={(ev) => { if (studentId) { resetAll(); } setSearch(ev.target.value); }} />
				{studentId ? <button type="button" className="wau-btn s mini" onClick={resetAll}>{S.buscarOtro}</button> : null}
			</div>
			{found && !studentId ? (
				<div className="wau-resultados">
					{found.length === 0 ? <span className="wau-sec" style={{ padding: 10 }}>{S.empty}</span> : found.map((s) => (
						<button key={"st" + s.id} type="button" onClick={() => { setStudentId(s.id); setEnrollmentId(null); setOptions(null); resetDecision(); setSearch(s.name); setFound(null); }}>
							<b>{s.name}</b><span className="wau-sec">{[s.code, s.phone].filter(Boolean).join(", ")}</span>
						</button>
					))}
				</div>
			) : null}
			{history && history.length > 1 ? (
				<div className="wau-chips" role="group">
					{history.map((h) => (
						<button key={"en" + h.id} type="button" className="wau-chip" aria-pressed={enrollmentId === h.id} onClick={() => { setEnrollmentId(h.id); resetDecision(); }}>
							{cursoCorto(h.courseName) || h.courseName}, {nombreHorario(h.scheduleName) || "—"}, {enrollmentStatusLabel(h.statusId, h.status)}
						</button>
					))}
				</div>
			) : history && history.length === 0 ? <span className="wau-sec">{S.empty}</span> : null}

			{e && options ? (
				<div className="wau-dos">
					<div className="wau-ficha">
						<div className="wau-ficha-cab"><h3>{e.studentName}</h3><span className="wau-pill ok">{enrollmentStatusLabel(e.statusId, e.statusLabel)}</span></div>
						<p className="wau-sec">{[e.identityCode, e.phone].filter(Boolean).join(", ")}</p>
						<dl className="wau-kv">
							<dt>{S.enrollment}</dt><dd>{cursoCorto(e.courseName) || e.courseName}{e.levelName ? ", " + e.levelName : ""}</dd>
							<dt>{S.schedule}</dt><dd>{nombreHorario(e.scheduleName) || "—"}{e.mentorName ? ", " + e.mentorName : ""}</dd>
							{plan ? <><dt>{S.planLabel}</dt><dd>{S.planLine(plan.n, plan.m, plan.dia)}</dd></> : null}
							<dt>{S.progressLabel}</dt><dd>{S.sessionsLine(e.sessionsTaken, sesionesTotal, attendance)}{e.sessionsToRecover > 0 ? <span className="wau-oro">, {S.lessSessions(e.sessionsToRecover)}</span> : null}</dd>
							{e.paidThrough ? <><dt>{S.paidThrough}</dt><dd>{e.paidThrough}</dd></> : null}
							{e.overdue > 0 ? <><dt>{S.overdue}</dt><dd className="wau-oro">{dinero(e.overdue, countryId)}</dd></> : null}
							{e.paidNotCoursed > 0 ? <><dt>{S.paidNotCoursed}</dt><dd className="wau-oro">{dinero(e.paidNotCoursed, countryId)}</dd></> : null}
						</dl>
					</div>

					<div className="wau-ficha wau-pila">
						<h3>{S.quienCambia}</h3>
						<div className="wau-chips" role="group">
							{chips.map((id) => (
								<button key={"ty" + id} type="button" className="wau-chip" aria-pressed={typeId === id} disabled={!!createdId}
									onClick={() => { setTypeId(id); setDestino(null); setBuscando(true); setToScheduleId(null); setToCourseId(null); }}>
									{id === CHIP_VENCIMIENTO ? "Cambio de Vencimiento" : TYPE_LABELS[id]}
								</button>
							))}
						</div>
						{pendingAlready ? <p className="wau-adv">{S.alreadyPending}</p> : null}

						{needsSchedule(typeId) ? (
							<div className="wau-pila">
								<span className="wau-lab">{isCourse ? S.cursoDestino : S.horarioDestino}</span>
								{destino && !buscando ? (
									<div className="wau-destino">
										<span><b>{destino.horario}</b>{destino.modulo ? ", " + destino.modulo : ""}{destino.maestro ? ", " + destino.maestro : ""}</span>
										<button type="button" className="wau-btn s mini" disabled={!!createdId} onClick={() => setBuscando(true)}>{S.cambiar}</button>
									</div>
								) : (
									<BuscadorHorarios key={"b" + typeId} compacto campusId={campusId} soloIds={soloIds} complemento={complemento} detalle={detalle} seleccionado={toScheduleId}
										onElegir={(h) => { setDestino(h); setToScheduleId(h.id); setToCourseId(isCourse ? cursoDe(h.id)?.id ?? null : null); setBuscando(false); }} />
								)}
							</div>
						) : null}

						{typeId === UPDATE_TYPE.RETIRED ? (
							<label className="wau-campo" style={{ maxWidth: 220 }}><span className="wau-lab">{S.lastSession}</span>
								<input className="wau-entrada wau-num" inputMode="numeric" value={lastSession} onChange={(ev) => setLastSession(ev.target.value.replace(/\D/g, ""))} /></label>
						) : null}
						{typeId === CHIP_VENCIMIENTO ? (
							<label className="wau-campo" style={{ maxWidth: 220 }}><span className="wau-lab">{S.dueDay}{due?.effectiveDueDay ? `, hoy ${due.effectiveDueDay}` : ""}</span>
								<input className="wau-entrada wau-num" inputMode="numeric" maxLength={2} value={dueDayDraft} onChange={(ev) => setDueDayDraft(ev.target.value.replace(/\D/g, ""))} /></label>
						) : null}

						{isReentry && toScheduleId && !createdId ? <InscripcionReingreso info={inscReingreso} value={insc} onChange={setInsc} busy={busy} /> : null}
						{isCourse && toScheduleId && !createdId && e ? (
							<PromocionDirecta campusId={campusId} studentId={e.studentId} studentName={e.studentName} scheduleIds={[toScheduleId]} value={promo} onChange={setPromo} busy={busy} marcar={false} />
						) : null}

						{typeId !== null && typeId !== CHIP_VENCIMIENTO && (target || e.overdue > 0) ? (
							<div className="wau-nota">
								{target ? <span>{S.goesInSession(target.classesGiven + 1)}{delta.more > 0 ? ", " + S.moreSessions(delta.more) : ""}{delta.less > 0 ? <b className="wau-oro">, {S.lessSessions(delta.less)}</b> : null}</span> : null}
								{e.overdue > 0 ? <span className="wau-oro">{S.overdueOf(dinero(e.overdue, countryId))}</span> : null}
							</div>
						) : null}

						{typeId !== null && !createdId ? (
							<>
								<textarea className="wau-entrada" rows={2} placeholder={S.notePlaceholder} aria-label={S.note} value={note} onChange={(ev) => setNote(ev.target.value)} />
								<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
									{typeId === CHIP_VENCIMIENTO ? (
										<button type="button" className="wau-btn p" disabled={busy || !dueOk || !noteOk} onClick={saveDue}>{S.saveDueDay}</button>
									) : (
										<>
											{canNow ? <button type="button" className="wau-btn p" disabled={busy} onClick={() => send(true)}>{S.applyNow}</button> : null}
											<button type="button" className={"wau-btn " + (canNow ? "s" : "p")} disabled={!canSend} onClick={() => send(false)}>{S.leavePending}</button>
										</>
									)}
								</div>
							</>
						) : null}
					</div>
				</div>
			) : null}

			{createdId ? (
				<div className="wau-pila">
					<UpdateDetail campusId={campusId} countryId={countryId} id={createdId} notifyError={(m) => notify(m, false)}
						onChanged={(m) => { notify(m); changed(); resetAll(); }} />
					<div className="wau-acciones" style={{ justifyContent: "flex-start", marginTop: 0 }}>
						<button type="button" className="wau-btn s" onClick={() => resetAll()}>{S.leavePending}</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default UnaAlumna;
